// TextEditorSettings — the LIVE-WIRING adapter for the Text block (WL-011b + WL-011d).
//
// Adopts the WL-011 TextEditor into the real craftjs Text node: reads node props via
// useNode(), maps them to the editor value (wiring.js — pure, unit-tested), routes
// style onChange back through setProp, and keeps the PrevStyles reducer copy in sync.
//
// WL-011d closes the rich-text PARITY GAP: the BODY is no longer a plain textarea. We
// inject the REAL Froala editor (Text/TextRichField, the SAME FroalaTextEditor the
// legacy Text block uses) via the TextEditor `renderRichField` slot, so the HTML written
// to `props.text` is BYTE-IDENTICAL to the legacy editor and stays compatible with the
// flag-gated sanitizeUserHtml render path. The Froala field writes `text` directly on
// its own debounce (mirroring the legacy TextForm onChange), so a style tweak never
// clobbers an in-flight rich-text edit (applyValueToProps includeBody:false on the
// style path).
//
// Wired directly as the Text block's craft.related.settings (Text/Text.jsx) —
// the sole Text settings editor.

import React from "react";
import { useNode } from "@craftjs/core";
import { useDispatch } from "react-redux";
import { updateTextStyles } from "../../../../redux/reducers/PrevStylesCopy/CodeblockDefaultStyles";
import { toggleSegmentSettings } from "../../../../redux/actions/segmentSettings";
import TextEditor from "../../BlockEditorPane/editors/TextEditor";
import TextRichField from "./TextRichField";
import { nodePropsToValue, applyValueToProps } from "./wiring";

const TextEditorSettings = ({ segmentName, onBack } = {}) => {
  const {
    id,
    actions: { setProp },
    props,
  } = useNode((node) => ({ id: node.id, props: node.data.props }));
  const dispatch = useDispatch();

  const value = nodePropsToValue(props);

  // STYLE path (heading/paragraph/line-spacing/padding). Never writes `text` so an
  // in-flight Froala body edit is never clobbered (includeBody: false).
  const handleStyleChange = (next) => {
    dispatch(toggleSegmentSettings(false));
    setProp((draft) => {
      applyValueToProps(next, draft, { includeBody: false });
      dispatch(
        updateTextStyles({
          headingProps: { ...draft.headingProps },
          paragraphProps: { ...draft.paragraphProps },
          lineHeight: draft.lineHeight,
          padding: { ...draft.padding },
          isApplyPaddingOnAllSides: draft.isApplyPaddingOnAllSides,
        })
      );
    });
  };

  // BODY path — the real Froala editor writes raw HTML straight to `props.text`,
  // debounced 500ms exactly like the legacy TextForm onChange. No PrevStyles churn.
  const handleBodyChange = (html) => {
    setProp((draft) => {
      draft.text = html;
    }, 500);
  };

  const renderRichField = () => (
    <TextRichField
      value={value.body}
      onChange={handleBodyChange}
      conversationKey={id}
      headingProps={props.headingProps}
      paragraphProps={props.paragraphProps}
      lineHeight={props.lineHeight}
    />
  );

  return (
    <TextEditor
      value={value}
      onChange={handleStyleChange}
      renderRichField={renderRichField}
      segmentName={segmentName}
      onBack={onBack}
    />
  );
};

export default TextEditorSettings;
